/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Ring buffer for per-atom position samples — the input to the
// mean-square-displacement (MSD) panel. Structurally identical to
// VelocityBuffer (it's a ring of 3-vectors per tracked atom); kept as
// a separate class so the DOS and MSD features can be enabled
// independently and the per-atom semantics ("this is a position", not
// "this is a velocity") stay legible at call sites.
//
// One MSD-specific twist: positions need to stay CONTINUOUS across
// periodic-boundary wraps. If an atom in a crystal MD run crosses a
// box face, its raw fractional position jumps by a full lattice vector
// and the MSD shoots up artificially. We detect that at push time —
// any per-component step larger than half the container length is
// treated as a wrap and the per-atom unwrap offset is accumulated so
// the stored position stays in unwrapped (R³) coordinates.
//
// For non-PBC simulations (the classical-MD chamber uses reflective
// walls), the wrap detection never trips and storage is identical to
// the raw atom positions.
//
// Like the other sample rings, the buffer always holds one contiguous,
// uniformly sampled run — a push that doesn't continue the stride clears it
// first (which also restarts the unwrap tracking, since a wrap can't be told
// from a gap anyway).

import { atomLayoutSignature, pickRepresentativeAtoms } from './VelocityBuffer';
import { continuesSampleRun, sampleSpacingChanged } from './sampleContinuity';

interface AtomLikeWithPosition {
  elementSymbol: string;
  position: { x: number; y: number; z: number };
}

export interface ContainerSize {
  lx: number;
  ly: number;
  lz: number;
}

export class PositionBuffer {
  readonly capacity: number;
  readonly maxAtoms: number;
  private rx: Float32Array[] | null = null;
  private ry: Float32Array[] | null = null;
  private rz: Float32Array[] | null = null;
  // Float64 stamps: Float32 loses sub-fs resolution past ~10⁷ fs (see DipoleCurrentBuffer).
  private t: Float64Array;
  private head = 0;
  private filled = false;
  // Stated sample spacing of the current run (null until a push says one).
  private runSpacingFs: number | null = null;
  // Per-tracked-atom accumulated wrap offsets. Added to incoming raw
  // positions so the stored series is continuous. Reset by prepare().
  private offsetX: Float32Array | null = null;
  private offsetY: Float32Array | null = null;
  private offsetZ: Float32Array | null = null;
  // Previous-raw-position cache (before unwrap), for wrap detection.
  // Same length as trackedIndices; populated lazily on the first push.
  private prevRawX: Float32Array | null = null;
  private prevRawY: Float32Array | null = null;
  private prevRawZ: Float32Array | null = null;
  private havePrev = false;
  trackedIndices: number[] = [];
  trackedElements: string[] = [];
  // Layout of the atoms array prepare() picked from; see isPreparedFor().
  private layoutSignature = '';

  constructor(capacity: number, maxAtoms: number) {
    if (capacity < 2) throw new Error(`PositionBuffer: capacity must be ≥ 2, got ${capacity}`);
    if (maxAtoms < 1) throw new Error(`PositionBuffer: maxAtoms must be ≥ 1, got ${maxAtoms}`);
    this.capacity = capacity;
    this.maxAtoms = maxAtoms;
    this.t = new Float64Array(capacity);
  }

  prepare(atoms: AtomLikeWithPosition[]): void {
    const indices = pickRepresentativeAtoms(atoms, this.maxAtoms);
    this.trackedIndices = indices;
    this.trackedElements = indices.map((i) => atoms[i].elementSymbol);
    this.layoutSignature = atomLayoutSignature(atoms);
    const n = indices.length;
    this.rx = indices.map(() => new Float32Array(this.capacity));
    this.ry = indices.map(() => new Float32Array(this.capacity));
    this.rz = indices.map(() => new Float32Array(this.capacity));
    this.offsetX = new Float32Array(n);
    this.offsetY = new Float32Array(n);
    this.offsetZ = new Float32Array(n);
    this.prevRawX = new Float32Array(n);
    this.prevRawY = new Float32Array(n);
    this.prevRawZ = new Float32Array(n);
    this.havePrev = false;
    this.head = 0;
    this.filled = false;
  }

  // Push one sample. `flatPositions` carries the tracked atoms'
  // raw (possibly wrapped) positions in trackedIndices order, packed
  // as [rx0, ry0, rz0, rx1, …]. `container` (optional) enables PBC
  // unwrap detection: any per-component step bigger than half the
  // matching box length is treated as a wrap and the unwrap offset is
  // updated. Pass undefined for non-PBC sims (reflective walls). `spacingFs`
  // is the sampler's stated sample spacing (stride × timestep); a change
  // starts a new run — see sampleContinuity.
  push(flatPositions: Float32Array, timeFs: number, container?: ContainerSize, spacingFs?: number): void {
    if (!this.rx || !this.ry || !this.rz) return;
    const n = this.trackedIndices.length;
    if (flatPositions.length < n * 3) return;
    if (
      sampleSpacingChanged(this.runSpacingFs, spacingFs) ||
      !continuesSampleRun(this.t, this.head, this.filled, this.capacity, timeFs)
    )
      this.clear();
    if (spacingFs !== undefined) this.runSpacingFs = spacingFs;
    const h = this.head;
    const halfLx = container ? container.lx * 0.5 : Infinity;
    const halfLy = container ? container.ly * 0.5 : Infinity;
    const halfLz = container ? container.lz * 0.5 : Infinity;
    for (let i = 0; i < n; i++) {
      const rawX = flatPositions[i * 3 + 0];
      const rawY = flatPositions[i * 3 + 1];
      const rawZ = flatPositions[i * 3 + 2];
      // Wrap detection — compare raw against the previous raw. A
      // jump > half-box ⇒ assume the atom wrapped; bump the offset
      // by ±L so the unwrapped trajectory stays continuous.
      if (this.havePrev && container) {
        const dx = rawX - this.prevRawX![i];
        const dy = rawY - this.prevRawY![i];
        const dz = rawZ - this.prevRawZ![i];
        if (dx > halfLx) this.offsetX![i] -= container.lx;
        else if (dx < -halfLx) this.offsetX![i] += container.lx;
        if (dy > halfLy) this.offsetY![i] -= container.ly;
        else if (dy < -halfLy) this.offsetY![i] += container.ly;
        if (dz > halfLz) this.offsetZ![i] -= container.lz;
        else if (dz < -halfLz) this.offsetZ![i] += container.lz;
      }
      this.rx[i][h] = rawX + this.offsetX![i];
      this.ry[i][h] = rawY + this.offsetY![i];
      this.rz[i][h] = rawZ + this.offsetZ![i];
      this.prevRawX![i] = rawX;
      this.prevRawY![i] = rawY;
      this.prevRawZ![i] = rawZ;
    }
    this.havePrev = true;
    this.t[h] = timeFs;
    this.head = (h + 1) % this.capacity;
    if (this.head === 0) this.filled = true;
  }

  clear(): void {
    this.head = 0;
    this.filled = false;
    this.runSpacingFs = null;
    this.havePrev = false;
    // Reset unwrap offsets too — a buffer reset usually means the
    // user reset the sim, so the trajectory restarts from t=0 with
    // atoms at their original positions.
    if (this.offsetX) this.offsetX.fill(0);
    if (this.offsetY) this.offsetY.fill(0);
    if (this.offsetZ) this.offsetZ.fill(0);
  }

  isPrepared(): boolean {
    return this.rx !== null;
  }

  // Prepared against THIS atom layout? See VelocityBuffer.isPreparedFor.
  isPreparedFor(atoms: AtomLikeWithPosition[]): boolean {
    return this.isPrepared() && this.layoutSignature === atomLayoutSignature(atoms);
  }

  size(): number {
    return this.filled ? this.capacity : this.head;
  }

  isEmpty(): boolean {
    return this.size() === 0;
  }

  durationFs(): number {
    const n = this.size();
    if (n < 2) return 0;
    const start = this.filled ? this.head : 0;
    const last = (this.head - 1 + this.capacity) % this.capacity;
    return this.t[last] - this.t[start];
  }

  averageDtFs(): number {
    const n = this.size();
    if (n < 2) return 0;
    return this.durationFs() / (n - 1);
  }

  getPerAtomComponents():
    | {
        rx: Float32Array;
        ry: Float32Array;
        rz: Float32Array;
        element: string;
      }[]
    | null {
    const n = this.size();
    if (n < 2 || !this.rx || !this.ry || !this.rz) return null;
    return this.trackedIndices.map((_, atomIdx) => {
      let rx: Float32Array;
      let ry: Float32Array;
      let rz: Float32Array;
      if (!this.filled) {
        rx = this.rx![atomIdx].subarray(0, n);
        ry = this.ry![atomIdx].subarray(0, n);
        rz = this.rz![atomIdx].subarray(0, n);
      } else {
        rx = new Float32Array(n);
        ry = new Float32Array(n);
        rz = new Float32Array(n);
        const tail = this.head;
        const firstChunk = n - tail;
        rx.set(this.rx![atomIdx].subarray(tail), 0);
        rx.set(this.rx![atomIdx].subarray(0, tail), firstChunk);
        ry.set(this.ry![atomIdx].subarray(tail), 0);
        ry.set(this.ry![atomIdx].subarray(0, tail), firstChunk);
        rz.set(this.rz![atomIdx].subarray(tail), 0);
        rz.set(this.rz![atomIdx].subarray(0, tail), firstChunk);
      }
      return { rx, ry, rz, element: this.trackedElements[atomIdx] };
    });
  }

  // Time extent (fs) of the buffered window, for retrospective segment resolution.
  windowExtentFs(): { minFs: number; maxFs: number; count: number } {
    const n = this.size();
    if (n === 0) return { minFs: 0, maxFs: 0, count: 0 };
    const start = this.filled ? this.head : 0;
    const last = (this.head - 1 + this.capacity) % this.capacity;
    return { minFs: this.t[start], maxFs: this.t[last], count: n };
  }

  // Per-atom (unwrapped) position series restricted to samples whose timestamp
  // lies in [t0Fs, t1Fs], plus the windowed averaged Δt and actual span. Null
  // when fewer than two samples fall inside. Feeds computeMsd over a SEGMENT.
  getPerAtomComponentsInWindow(
    t0Fs: number,
    t1Fs: number,
  ): {
    atoms: { rx: Float32Array; ry: Float32Array; rz: Float32Array; element: string }[];
    dtFs: number;
    startFs: number;
    endFs: number;
  } | null {
    const n = this.size();
    if (n < 2 || !this.rx || !this.ry || !this.rz) return null;
    const start = this.filled ? this.head : 0;
    const idxs: number[] = [];
    let startFs = Infinity;
    let endFs = -Infinity;
    for (let k = 0; k < n; k++) {
      const idx = (start + k) % this.capacity;
      const tk = this.t[idx];
      if (tk < t0Fs || tk > t1Fs) continue;
      idxs.push(idx);
      if (tk < startFs) startFs = tk;
      if (tk > endFs) endFs = tk;
    }
    if (idxs.length < 2) return null;
    const atoms = this.trackedIndices.map((_, a) => {
      const rx = new Float32Array(idxs.length);
      const ry = new Float32Array(idxs.length);
      const rz = new Float32Array(idxs.length);
      for (let j = 0; j < idxs.length; j++) {
        rx[j] = this.rx![a][idxs[j]];
        ry[j] = this.ry![a][idxs[j]];
        rz[j] = this.rz![a][idxs[j]];
      }
      return { rx, ry, rz, element: this.trackedElements[a] };
    });
    return { atoms, dtFs: (endFs - startFs) / (idxs.length - 1), startFs, endFs };
  }
}
